import * as React from 'react';
import classnames from 'classnames';
import YouTube from 'react-youtube';
import {useEffect, useState} from 'react';
import { FormattedMessage } from 'react-intl';

import {EpiImage} from "features/search/types";
import {IContentBlock} from 'features/blocks/types';
import {ImageSizes} from '@organisms/Shell/image-size-enum';
import {useModal} from 'features/modal/hooks/useModal';
import {sanitizeHtml} from 'features/services/utils/sanitize-html';
import {useMetaStore, selectCookieSettings} from 'features/meta/store/useMetaStore';
import { getFormattedMessage } from 'features/services/utils/language-helper';
import {gtmEventsVideoStart} from 'features/services/googleTagManager/events';
import {ParagraphSize, Margin, TextColor} from 'features/ui/enums';

import 'components/blocks/ImageAndVideo/VideoBlock/style.scss';

import { P } from '@atoms/Text/P';
import { Icon } from '@atoms/Media/Icon';
import {MainGrid} from '@atoms/Layout/MainGrid';
import {PlayPause} from '@atoms/LinksAndButtons/PlayPause';
import {AsyncImage} from '@atoms/Media/AsyncImage';
import {TemplateLoader} from '@atoms/TemplateLoader';
import { FaqQuestionBlock } from '../../FAQ/FaqQuestionBlock';
import {AcceptThirdPartModal} from "components/modals/General/AcceptThirdPartyModal";

type Props = IContentBlock<VideoBlockType>;
interface VideoBlockType {
    videoUrl: string;
    title?: string;
    image: EpiImage;
    renderOnPage?: boolean;
    hideDescriptionOnPage?: boolean;
    description?: string;
    scriptText?: string;
}

export const VideoBlock: React.FunctionComponent<Props> = ({data, id}) => {
    const { open } = useModal();
    const [videoId, setVideoId] = useState<string>('');
    const [isVideoVisible, _setIsVideoVisible] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [showOnPageVideo, setShowOnPageVideo] = useState<boolean>(false);
    const userCookieSettings = useMetaStore(selectCookieSettings);
    const [scriptIsOpen, setScriptIsOpen] = useState<boolean>(false);

    const videoOpts = {
        height: '669',
        width: '1440',
        playerVars: {
            'origin': window.location.origin,
            'rel': 0,
            'modestbranding': 1,
        }
    };

    useEffect(() => {
        if(!data.videoUrl) return;
        setVideoId(getVideoId(data.videoUrl));
    }, [data.videoUrl]);

    const getVideoId = (url: string) => {
        const regex = /(?:https?:\/\/(?:[a-z]+.)?)(?:youtu\.?be(?:\.com)?\/)(?:embed\/)?(?:(?:(?:(?:watch\?)?(?:time_continue=(?:[0-9]+))?.+v=)?([a-zA-Z0-9_-]+))(?:\?t=(?:[0-9a-zA-Z]+))?)/;
        const match = url.match(regex);
        return (match !== null) ? match[1] : '';
    };

    const onVideoPlayerReady = (event) => {
        setLoading(false);
        gtmEventsVideoStart(window.location.pathname)();
        event.target.playVideo();
    };

    const toggleOverlay = () => {
        if(!userCookieSettings.thirdParty) {
            open(
                <AcceptThirdPartModal
                    renderOnAccept={(
                        renderVideo()
                    )}
                />,
                { fullscreen: true, triggerElementId: 'video-' + id, ariaLabel: getFormattedMessage('jm.components.AcceptThirdPartyModal.header') }
            );
            return;
        }

        if(data.renderOnPage) {
            setShowOnPageVideo(true);
        } else {
            open(
                renderVideo(),
                { fullscreen: true, triggerElementId: 'video-' + id, ariaLabel: getFormattedMessage('jm.components.VideoBlock.ariaLabel') }
            );
        }
    };
    const handleOpen = () => {
        setScriptIsOpen(!scriptIsOpen);
    };

    const renderScript = () => {
        if(!data.scriptText) return null;
        return (
            <div 
                className="video-block__script" 
                onClick={handleOpen} 
                aria-expanded={scriptIsOpen} 
                aria-controls={'script-' + id} 
            >
                <P size={ParagraphSize.L} margin={Margin.NONE} className="video-block__script__headline">
                    <FormattedMessage id="jm.components.VideoBlock.scriptHeadline" />
                    <Icon name="chevron" up={!scriptIsOpen} />
                </P>
                <div 
                    className={scriptClasses} 
                    dangerouslySetInnerHTML={{__html: data.scriptText}} 
                    id={'script-' + id} 
                    aria-hidden={!scriptIsOpen}
                />
            </div>
        )
    };
    const scriptClasses = classnames('video-block__script__text', {
            'video-block__script__text--open': scriptIsOpen,
        });

    const renderVideo = () => {
        return (
            <div className="video-wrapper">
                <YouTube
                    videoId={videoId}
                    opts={videoOpts}
                    onReady={onVideoPlayerReady}
                    containerClassName="video-container"
                />
                {data.description && 
                    <P className="video-block__description" margin={Margin.S} dangerouslySetInnerHTML={{__html: sanitizeHtml(data.description)}} />
                }
                {!data.renderOnPage && data.scriptText &&
                    <FaqQuestionBlock
                        data={{
                            question: getFormattedMessage('jm.components.VideoBlock.scriptHeadline'),
                            answer: data.scriptText,
                        }}
                        component={''}
                        renderStyle={''}
                    />
                }
            </div>
        )
    };

    if(videoId === '') return null;
    return (
        <div className="video-block" id={id}>
            <MainGrid fullwidth>
                {isVideoVisible && loading && <TemplateLoader />}

                {(data.renderOnPage && showOnPageVideo) &&
                    renderVideo()
                }

                {!showOnPageVideo && 
                    <div className="video-block__overlay" onClick={toggleOverlay}>
                        {data.title &&
                            <P className="video-block__title--desktop" size={ParagraphSize.L} color={TextColor.INVERSE}>
                                {data.title}
                            </P>
                        }
                        <PlayPause
                            playing={false}
                            id={'video-' + id} 
                            onClick={toggleOverlay}
                        />
                        <div className="video-block__wrapper">
                            <AsyncImage
                                image={data.image}
                                fullWidth
                                quality={ImageSizes.SIZE_1200}
                                alt={data.title}
                            />
                        </div>
                    </div>
                }
                {!showOnPageVideo && data.description && !data.hideDescriptionOnPage &&
                    <P className="video-block__description" margin={Margin.S} dangerouslySetInnerHTML={{__html: sanitizeHtml(data.description)}} />
                }
                {data.renderOnPage &&
                    renderScript()
                }
            </MainGrid>
        </div>
    )
};
